import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send message. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
      <div>
        <p className="text-xs uppercase tracking-[0.18em] text-blue-800 dark:text-blue-400 font-semibold mb-3">Contact Us</p>
        <h1 className="font-display text-4xl lg:text-5xl font-bold text-blue-950 dark:text-blue-50 leading-tight">We're here to help your family</h1>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">Questions about care, courses, or joining our marketplace? Reach out and our Columbus team will respond within one business day.</p>
        <div className="mt-8 space-y-5">
          {[
            { icon: MapPin, label: "Visit", value: "5310 E. Main St, Columbus, Ohio 43213" },
            { icon: Phone, label: "Call", value: "(614) 555-0110" },
            { icon: Mail, label: "Email", value: "care@t5care.com" },
            { icon: Clock, label: "Hours", value: "Mon–Fri 8am–6pm · On-call care 24/7" },
          ].map((c) => (
            <div key={c.label} className="flex items-start gap-4">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-100 text-blue-800 shrink-0"><c.icon className="w-5 h-5" /></span>
              <div><p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{c.label}</p><p className="text-slate-800 dark:text-slate-100">{c.value}</p></div>
            </div>
          ))}
        </div>
      </div>

      <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} data-testid="contact-form"
        className="bg-white dark:bg-blue-900/30 rounded-2xl p-8 shadow-lg border border-blue-900/5 self-start w-full">
        <h2 className="font-display text-2xl font-semibold text-blue-950 dark:text-blue-50 mb-6">Send us a message</h2>
        <div className="space-y-4">
          <div><Label htmlFor="c-name">Name</Label><Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" /></div>
          <div><Label htmlFor="c-email">Email</Label><Input id="c-email" type="email" data-testid="contact-email-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
          <div><Label htmlFor="c-msg">Message</Label><Textarea id="c-msg" data-testid="contact-message-input" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" /></div>
        </div>
        <Button type="submit" disabled={loading} data-testid="contact-submit-button" className="mt-6 w-full h-12 bg-blue-900 hover:bg-blue-950 text-white">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Send Message"}
        </Button>
      </motion.form>
    </div>
  );
}
